<script setup lang="ts">

import {computed, inject, ref, watch} from "vue";
import {useStore} from "vuex";
import {ParametersVariations} from "@/assets/ts/parametersVariations";
import ItemV3StonesParameterLoading from "@/components/itemV3/parameter/stones/parameter/itemV3StonesParameterLoading.vue";

const props = defineProps<{ groupIdx: number }>();
const emit = defineEmits(["patch"]);

const store = useStore();

const loadItemVariants = inject<any>("loadItemVariants");

const itemIdx = inject<any>("itemIdx");
const session = inject<any>("session");

/**
 * session
 *
 * Transferred from itemV3StonePresetsStoneSize.vue: configureAll/itemIdx are wired into
 * ParametersVariations the same way, but - like itemV3StonesPosition.vue - the variant
 * is fetched per stone group via getItemVariants(itemIdx, "stones.<groupIdx>.stone_size")
 * instead of the fixed "stones.0.stone_size" used there.
 */

const configureAll = computed(() => store.getters["session/getConfigureAll"])
const variantIdx = ""

const parametersVariations = new ParametersVariations(variantIdx, configureAll, session, itemIdx, loadItemVariants)

/**
 * stone size - stones.<groupIdx>.stone_size
 */

const stoneSize = ref({
  "stone_size": {}
} as any)

const getStoneSizeData = (groupIdy: number) => {
  if (groupIdy >= 0) {
    stoneSize.value.stone_size = parametersVariations.getItemVariants(itemIdx.value, "stones." + groupIdy + ".stone_size")
  }
}

watch(() => props.groupIdx, (groupIdy) => {
  getStoneSizeData(groupIdy)
}, {immediate: true})

watch(session, () => {
  getStoneSizeData(props.groupIdx)
}, {deep: true})

/**
 * Patch
 */

const handleValuePatch = (actionIdx: string, value: any) => {
  value = (typeof value == "string") ? value : value.idx
  emit('patch', {
    action: actionIdx,
    value: value,
    certificated_diamond: value.certificated_diamond
  });
};

/**
 * stone size - step slider (transferred from itemV3Grooves.vue's "groove details - width")
 */

const sizeValues = computed(() => stoneSize.value.stone_size[0]?.values || [])

const sizeIdx = computed(() => {
  const idx = sizeValues.value.findIndex((v: any) => v.match)
  return idx === -1 ? 0 : idx
})

// local slider position, so dragging the handle only updates the UI - the api
// call is only sent once the user drops (releases) the slider, see onSizeRangeChange
const sliderSizeIdx = ref(0)

watch(sizeIdx, (idx) => {
  sliderSizeIdx.value = idx
}, {immediate: true})

const sizeSliderPercent = computed(() => {
  if (sizeValues.value.length < 2) return 0
  return (sliderSizeIdx.value / (sizeValues.value.length - 1)) * 100
})

const currentSize = computed(() => sizeValues.value[sliderSizeIdx.value])

const stepSize = (delta: number) => {
  if (!stoneSize.value.stone_size[0]) return
  const newIdx = sizeIdx.value + delta
  if (newIdx < 0 || newIdx >= sizeValues.value.length) return
  handleValuePatch(stoneSize.value.stone_size[0].idx, sizeValues.value[newIdx])
}

const onSizeRangeInput = (event: Event) => {
  sliderSizeIdx.value = parseInt((event.target as HTMLInputElement).value)
}

const onSizeRangeChange = (event: Event) => {
  if (!stoneSize.value.stone_size[0]) return
  const idx = parseInt((event.target as HTMLInputElement).value)
  const value = sizeValues.value[idx]
  if (value) handleValuePatch(stoneSize.value.stone_size[0].idx, value)
}

</script>

<template>
  <div v-if="stoneSize.stone_size[0]" class="w-full px-2 first:pl-0 last:pr-0">
    <div class="flex items-center gap-1">
      <button
          type="button"
          class="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-[#e7ddcc] text-[#8f8a80] transition-colors hover:bg-[#f3f0e9] hover:text-brand-main disabled:cursor-not-allowed disabled:opacity-30"
          :disabled="sizeIdx <= 0"
          @click="stepSize(-1)"
      >
        −
      </button>
      <span
          class="flex-1 rounded-xl border border-[#e7ddcc] py-1.5 text-center text-xs font-medium text-parameter-title">
        {{ currentSize?.title }}
      </span>
      <button
          type="button"
          class="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full border border-[#e7ddcc] text-[#8f8a80] transition-colors hover:bg-[#f3f0e9] hover:text-brand-main disabled:cursor-not-allowed disabled:opacity-30"
          :disabled="sizeIdx >= sizeValues.length - 1"
          @click="stepSize(1)"
      >
        +
      </button>
    </div>
    <div class="relative mt-4 flex h-4 items-center select-none">
      <!-- track -->
      <div class="pointer-events-none absolute inset-x-0 h-[3px] rounded-full bg-[#e7ddcc]">
        <div class="h-full rounded-full bg-brand-main" :style="{ width: sizeSliderPercent + '%' }"></div>
      </div>
      <!-- thumb -->
      <div
          class="pointer-events-none absolute top-1/2 size-4 rounded-full bg-brand-main shadow-md"
          :style="{ left: sizeSliderPercent + '%', transform: 'translate(-50%, -50%)' }"
      ></div>
      <!-- native range input, kept invisible but interactive -->
      <input
          type="range"
          class="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
          min="0"
          :max="sizeValues.length - 1"
          step="1"
          :value="sliderSizeIdx"
          @input="onSizeRangeInput"
          @change="onSizeRangeChange"
      />
    </div>
    <div class="mt-1 flex justify-between text-[10px] text-parameter-text">
      <span>{{ sizeValues[0]?.title }}</span>
      <span>{{ sizeValues[sizeValues.length - 1]?.title }}</span>
    </div>
  </div>
  <item-v3-stones-parameter-loading v-else/>
</template>

<style scoped>
</style>
